/**
 * Report persistence helpers used by ReportBuilderPage.
 * Pure-ish helpers — no React, no JSX. Easier to test in isolation.
 */
import { api } from '@/lib/api';

/**
 * Capture an html2canvas thumbnail of a DOM node and downscale to ~800px wide JPEG.
 * Returns a base64 data URL or '' if capture failed.
 */
export async function captureCanvasThumbnail(canvasEl) {
  if (!canvasEl) return '';
  try {
    // html2canvas runs on the main thread and its cost grows with the number of
    // DOM nodes it has to clone + rasterize. On complex dashboards this becomes a
    // multi-second "long task" that triggers the browser's "Page Unresponsive"
    // dialog. The thumbnail is purely cosmetic (a preview card in the reports
    // list), so skip the capture entirely for heavy canvases — those fall back to
    // a placeholder card instead of freezing the tab.
    const nodeCount = canvasEl.getElementsByTagName('*').length;
    if (nodeCount > 4000) return '';
    const w = Math.min(canvasEl.scrollWidth, 1200);
    const h = Math.min(canvasEl.scrollHeight, 750);
    const { default: html2canvas } = await import('html2canvas');
    const thumbCanvas = await html2canvas(canvasEl, {
      backgroundColor: '#09090b', scale: 1, logging: false, useCORS: true,
      width: w, height: h, windowWidth: w, windowHeight: h,
      ignoreElements: (el) => el.getAttribute && el.getAttribute('data-thumb-ignore') === 'true',
    });
    const outW = 800;
    const outH = Math.round((h / w) * outW);
    const offscreen = document.createElement('canvas');
    offscreen.width = outW;
    offscreen.height = outH;
    const ctx = offscreen.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(thumbCanvas, 0, 0, outW, outH);
    let url = offscreen.toDataURL('image/jpeg', 0.85);
    if (url.length > 400000) url = offscreen.toDataURL('image/jpeg', 0.7);
    return url;
  } catch (err) {
     
    console.warn('Thumbnail capture failed:', err);
    return '';
  }
}

/**
 * Build the canonical "save" payload from in-memory builder state.
 * Accepts a single state object so call-sites stay small.
 */
export function buildReportPayload(state, { thumbnail = '', includeReportConfig = false } = {}) {
  const {
    reportName, reportDesc, modelId, visuals, showGrid, showLegend,
    refLineValue, refLineLabel, condRules, secondYAxisConfig, dateParts,
    reportTheme, gridLayouts, parameters, paramValues, globalFilterPosition,
    visualConfigs, fieldOverrides, pages, pageVisuals, pageNavStyle, aiContents, annotations,
    mobileLayout, designerMode, reportPageSize, reportOrientation, reportMargins,
    reportHeader, reportFooter, reportTableSpanPages, reportTableRepeatHeader, reportPages,
    slicerSelections, globalFilterValues, globalFilterDims, slicerDisplayModes, slicerSettings, cascadeSlicers,
    pageFilterDims, pageFilterValues,
    importSessionId,
  } = state;

  const firstVisual = visuals[0] || {};

  const baseConfig = {
    visuals, showGrid, showLegend, refLineValue, refLineLabel, condRules,
    colDim: firstVisual.colDim || '', secondYAxisConfig, dateParts,
    reportTheme, gridLayouts, parameters, paramValues, globalFilterPosition,
    visualConfigs, fieldOverrides, pages, pageVisuals, pageNavStyle, aiContents,
    slicerSelections, globalFilterValues, globalFilterDims, slicerDisplayModes, slicerSettings, cascadeSlicers,
    pageFilterDims, pageFilterValues,
    ...(importSessionId ? { import_session_id: importSessionId } : {}),
  };

  const config = includeReportConfig
    ? {
        ...baseConfig, annotations, mobileLayout, designerMode,
        reportPageSize, reportOrientation, reportMargins, reportHeader, reportFooter,
        reportTableSpanPages, reportTableRepeatHeader, reportPages,
      }
    : baseConfig;

  return {
    name: reportName,
    description: reportDesc,
    visualization_type: firstVisual.vizType || 'table',
    query_definition: {
      model_id: modelId,
      dimensions: [
        ...(firstVisual.rowDims || []),
        ...(firstVisual.colDim ? [firstVisual.colDim] : []),
      ],
      measures: firstVisual.valueMeasures || [],
      filters: [],
      sort_dir: 'asc',
      limit: 1000,
      column_dimension: firstVisual.colDim || '',
    },
    config,
    ...(thumbnail ? { thumbnail } : {}),
  };
}

/** Save (create-or-update) the report and write a version. Returns the resolved report id. */
export async function persistReport({ state, reportId, versionDesc, thumbnail }) {
  const data = buildReportPayload(state, { thumbnail, includeReportConfig: true });
  if (versionDesc != null) data.version_description = versionDesc;
  let rid = reportId;
  if (rid) {
    await api.updateReport(rid, data);
  } else {
    const firstVisual = state.visuals[0] || {};
    const r = await api.createReport({
      name: state.reportName,
      description: state.reportDesc,
      visualization_type: firstVisual.vizType || 'table',
      report_type: state.designerMode === 'report' ? 'pixel_perfect' : undefined,
    });
    await api.updateReport(r.report_id, data);
    rid = r.report_id;
  }
  await api.createVersion('report', rid, versionDesc || 'Saved');
  // Read back the authoritative monotonic version number (PUT increments _version).
  let version = null;
  try { const fresh = await api.getReport(rid); version = fresh?._version ?? null; } catch (_) { /* non-fatal */ }
  return { reportId: rid, version };
}

/** Clone the in-memory state into a brand-new report. Returns the new report id. */
export async function cloneCurrentReport({ state }) {
  const cloneName = state.reportName + ' (Copy)';
  const data = buildReportPayload({ ...state, reportName: cloneName }, { includeReportConfig: false });
  const firstVisual = state.visuals[0] || {};
  const r = await api.createReport({
    name: cloneName,
    description: state.reportDesc,
    visualization_type: firstVisual.vizType || 'table',
  });
  await api.updateReport(r.report_id, data);
  // Write an initial version so clones match the parity of saved reports.
  try { await api.createVersion('report', r.report_id, 'Cloned from ' + state.reportName); } catch {}
  return r.report_id;
}
